/**
 * マスタ変更履歴の画面 (マスタ変更履歴 SBI 7)。
 *
 * URL による階層の切り替えを行う。
 *   ① 変更一覧 / ② 対象データの履歴一覧 / ③ 履歴詳細
 * 判定は `resolveMasterHistoryView` (URL だけで決まる純粋関数) に任せる。
 *
 * `entity_key` は日本語や `#` を含む内部の識別子で、画面の文言には出さない。
 * 値は `data-*` 属性にだけ載せる (テストと、他の画面が同じ値を参照できるようにするため)。
 */
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { MasterHistoryType } from '../api/types';
import { MasterHistoryDetailScreen } from '../components/MasterHistoryDetailScreen';
import { MasterHistoryEntityScreen } from '../components/MasterHistoryEntityScreen';
import { MasterHistoryListScreen } from '../components/MasterHistoryListScreen';
import type { AppConfig } from '../config';
import { usePermissions } from '../contexts/PermissionContext';
import { MASTER_HISTORY_FEATURE_BY_TYPE } from '../permissions/masterHistoryFeatures';

import {
  MASTER_HISTORY_NAV_STATE,
  isOpenedFromNav,
  masterTypeOfEntityKey,
  resolveMasterHistoryView,
} from './masterHistoryView';

export interface MasterHistoryPageProps {
  config: AppConfig;
}

export function MasterHistoryPage(props: MasterHistoryPageProps): React.JSX.Element {
  const { config } = props;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { has } = usePermissions();
  const view = resolveMasterHistoryView(searchParams);
  // タブから入ったときは遷移元が無いので、「閉じる」「遷移元へ戻る」を出さない。
  // 履歴画面内の遷移 (①→②→③ など) にも state を引き継ぎ、途中で導線が復活しないようにする。
  const fromNav = isOpenedFromNav(location.state);

  const allowedTypes = (Object.keys(MASTER_HISTORY_FEATURE_BY_TYPE) as MasterHistoryType[]).filter(
    (type) => has(MASTER_HISTORY_FEATURE_BY_TYPE[type]),
  );
  const onNavigate = (path: string): void => {
    void navigate(path, fromNav ? { state: MASTER_HISTORY_NAV_STATE } : undefined);
  };

  if (view.kind === 'list') {
    return (
      <MasterHistoryListScreen
        config={config}
        masterType={view.masterType}
        allowedTypes={allowedTypes}
        onNavigate={onNavigate}
        hideSourceBack={fromNav}
      />
    );
  }

  // 種別が判定できる (entity_key の先頭が既知の種別) 場合だけ、権限が無ければ API を呼ばない。
  // 判定できないときは API (403 / 空配列 / 404) に任せる。
  const entityType = masterTypeOfEntityKey(view.entityKey);
  const forbidden = entityType !== undefined && !allowedTypes.includes(entityType);

  if (view.kind === 'entity') {
    return (
      <MasterHistoryEntityScreen
        config={config}
        entityKey={view.entityKey}
        forbidden={forbidden}
        invalidRev={view.invalidRev}
        onNavigate={onNavigate}
        hideSourceBack={fromNav}
      />
    );
  }

  return (
    // 取得条件 (対象・版) が変わったら作り直し、前の版の表示や比較の開閉状態を持ち越さない。
    <MasterHistoryDetailScreen
      key={`${view.entityKey}|${view.rev}`}
      config={config}
      entityKey={view.entityKey}
      version={view.rev}
      forbidden={forbidden}
      onNavigate={onNavigate}
      hideSourceBack={fromNav}
    />
  );
}
